import React, { useEffect, Suspense } from 'react'
import { ConfigProvider, Spin } from 'antd'
import zhCN from 'antd/locale/zh_CN'
import Layout from '@/components/Layout'
import './App.less'
import AuthorizationBoundary from '@/components/AuthorizationBoundary'
import { Navigate, useNavigate, useLocation } from 'react-router-dom'
import { clearUser, getLastAuthActivity, getMustChangePassword, getToken, hasAuthIdleExpired, markAuthActivity } from './utils/auth'

// Ant Design interpolates these form validation placeholders.
/* eslint-disable no-template-curly-in-string */
const validateMessages = {
  required: '请输入或选择${label}',
  types: {
    email: '${label}格式不正确',
    number: '${label}必须是数字',
  },
  string: {
    min: '${label}至少需要${min}个字符',
    max: '${label}不能超过${max}个字符',
  },
}

/* eslint-enable no-template-curly-in-string */

function App () {
  const location = useLocation()
  const navigate = useNavigate()
  useEffect(() => {
    const path = location.pathname === '/' ? '/home/dashboard' : location.pathname
    if (!getToken()) {
      navigate(`/auth/login?path=${encodeURIComponent(path)}`, { replace: true })
      return
    }
    if (location.pathname === '/') {
      navigate('/home/dashboard', { replace: true })
    }
  }, [location.pathname, navigate])

  useEffect(() => {
    if (location.pathname === '/auth/login' || !getToken()) {
      return
    }

    const currentPath = `${location.pathname}${location.search}${location.hash}`
    const redirectToLogin = () => {
      clearUser()
      navigate(`/auth/login?path=${encodeURIComponent(currentPath)}`, { replace: true })
    }
    const checkIdle = () => {
      if (!getToken()) {
        return
      }
      if (hasAuthIdleExpired()) {
        redirectToLogin()
      }
    }
    const handleActivity = () => {
      if (!getToken()) {
        return
      }
      if (hasAuthIdleExpired()) {
        redirectToLogin()
        return
      }
      markAuthActivity()
    }
    if (!getLastAuthActivity()) {
      markAuthActivity()
    }
    checkIdle()

    const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove']
    activityEvents.forEach((eventName) => window.addEventListener(eventName, handleActivity, { passive: true }))
    const timer = window.setInterval(checkIdle, 60 * 1000)

    return () => {
      window.clearInterval(timer)
      activityEvents.forEach((eventName) => window.removeEventListener(eventName, handleActivity))
    }
  }, [location.hash, location.pathname, location.search, navigate])

  if (getToken() && getMustChangePassword() && location.pathname !== '/profile' && location.pathname !== '/auth/login') {
    return <Navigate to="/profile" replace />
  }

  return (
    <ConfigProvider
      locale={zhCN}
      form={{ validateMessages }}
      theme={{
        token: {
          fontSize: 14,
          fontSizeHeading1: 28,
          fontSizeHeading2: 24,
          fontSizeHeading3: 20,
          fontSizeHeading4: 16,
          fontSizeHeading5: 14,
          borderRadius: 12,
          borderRadiusSM: 8,
          borderRadiusLG: 16,
          colorPrimary: '#4f46e5',
          colorSuccess: '#16a34a',
          colorWarning: '#d97706',
          colorError: '#dc2626',
          colorInfo: '#0284c7',
          colorBgLayout: '#f5f5f7',
          colorBgContainer: '#ffffff',
          colorBorderSecondary: '#e2e8f0',
          colorTextSecondary: '#64748b',
          controlHeight: 36,
          controlHeightSM: 28,
          controlHeightLG: 44,
          boxShadow: '0 1px 3px rgba(0,0,0,0.02), 0 4px 12px rgba(0,0,0,0.03)',
          boxShadowSecondary: '0 4px 16px rgba(0,0,0,0.06), 0 1px 4px rgba(0,0,0,0.04)',
        },
        components: {
          Table: {
            headerBg: '#fafafa',
            headerColor: '#475569',
            headerSplitColor: 'rgba(0,0,0,0.05)',
            rowHoverBg: 'rgba(79,70,229,0.03)',
            borderColor: 'rgba(0,0,0,0.05)',
          } as any,
          Card: {
            paddingLG: 24,
          } as any,
          Menu: {
            itemHeight: 42,
            iconSize: 16,
          } as any,
          Button: {
            primaryShadow: '0 2px 8px rgba(79,70,229,0.3)',
          } as any,
        },
      }}
    >
      <Spin spinning={false}>
        <AuthorizationBoundary><Suspense fallback={<Spin />}><Layout /></Suspense></AuthorizationBoundary>
      </Spin>
    </ConfigProvider>
  )
}

export default App
